<template>
    <v-layout row> 
        <v-flex xs12>
            <v-card class="elevation-1 transparent">
                <v-toolbar class="transparent" flat height="37">
                    <v-spacer></v-spacer>
                    <v-icon dark @click="dialogCustomizeDashboard=true" class="clickable" title="Customize Dashboard">dashboard_customize</v-icon>
                </v-toolbar>
            </v-card>

            <!-- error handler -->
            <v-alert v-if="error" :value="true" color="error" icon="error" outline class="mt-8">
                Error encountered. Please contact support. Error details: {{errorMessage}}
            </v-alert> 

            <!-- eDocuments Banner - applicable to all -->
            <no-response-edocs></no-response-edocs>            

            <!-- company announcements - applicable to all -->
            <announcements-banner></announcements-banner>

            <!-- only timesheet reminders, already filtered in the backend -->
            <v-alert v-for="(item,index) in reminders" :key="index" :value="true" :color="item.alertType" :icon="item.icon" outline class="mt-8 rounded-4">
                <span v-html="item.description" @click="navigateToPage(item)" class="alert-link"></span>
            </v-alert>

            <v-layout row wrap grid-list-md>

                <!-- Dynamic Dashboard Widgets -->
                <v-flex xs12 sm8>
                    <!-- Removed class="pa-1" from the loop to prevent layout collision -->
                    <div
                        v-for="widget in dashboardWidgetsLarge"
                        :key="widget.widgetId"
                        class="mt-3"
                    >
                        <component
                            :is="widgetComponentMap[widget.widgetCode]"
                            :widget="widget"
                        />
                    </div>
                </v-flex>
                <v-flex xs12 sm4>
                    <div
                        v-for="widget in dashboardWidgetsSmall"
                        :key="widget.widgetId"
                        class="mt-3"
                    >
                        <component
                            :is="widgetComponentMap[widget.widgetCode]"
                            :widget="widget"
                        />
                    </div>
                </v-flex>
            </v-layout>

        </v-flex>

        <v-dialog v-model="dialogEnableDeviceLogin" width="600" persistent>   
            <enable-device-login :accessedFromMyDashboard="true" @success="onEnableDeviceSuccess" @fail="onEnableDeviceFailure" :dialogEnableDeviceLogin.sync="dialogEnableDeviceLogin"></enable-device-login>
        </v-dialog>
        <v-dialog v-model="dialogOkOnly" width="500" persistent>
            <ok-only :title="dialogOkOnlyTitle" :msg="dialogOkOnlyMsg" :dialogOkOnly.sync="dialogOkOnly"></ok-only>
        </v-dialog>
        <v-dialog v-model="dialogCustomizeDashboard" width="700" persistent>
            <customize :dialogCustomizeDashboard.sync="dialogCustomizeDashboard"></customize>
        </v-dialog>

    </v-layout>
</template>
<script>

import notificationsApi from '@/api/notifications'
import { getHideDeviceLoginPrompt, clearDeviceLoginCooldowns } from '@/helpers/user-prefs'
import { isPlatformAuthenticatorAvailable } from '@/helpers/webauthn'

export default {  
    name: 'MyDashboard',
    metaInfo: {
        title: 'My Dashboard',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'My Dashboard' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
        ]
    },
    components: {
        'announcements-banner': () => import('@/components/CompanyAnnouncements/CompanyAnnouncementsBanner'),
        'enable-device-login': () => import('@/components/DeviceLogin/EnableDeviceLogin'),
        'ok-only': () => import('@/components/BasicPopups/OkOnly'),
        'no-response-edocs': () => import('@/components/EDocuments/NoResponseBanner'),

        //dashboard widgets
        'my-approvals': () => import('@/components/MyDashboard/MyApprovals'),
        'my-favorites': () => import('@/components/MyDashboard/MyFavorites'),
        'my-requests': () => import('@/components/MyDashboard/MyRequests'),
        'quick-actions': () => import('@/components/MyDashboard/QuickActions'),
        'recent-time-logs': () => import('@/components/MyDashboard/RecentTimeLogs'),
        'customize': () => import('@/components/MyDashboard/CustomizeDashboard'),
    },
    data () {
        return {
            error: false,
            errorMessage: '',
            dialogEnableDeviceLogin: false,
            dialogOkOnly: false,
            dialogOkOnlyTitle: null,
            dialogOkOnlyMsg: null,
            dialogCustomizeDashboard: false,
            loading: false,
            now: new Date(),    // Track the current time reactively
            reminders: []
        }
    },
    methods: { 
        navigateToPage(item) {
            //current reminder is for Timesheet only.
            //once other pages reminder are implemented, need to check Type to determine page
            this.$router.push(`/myattendance/timesheet/${item.cutOffId}/${item.userId}`)
        },
        async getMyReminders() {
            try {
                const response = await notificationsApi.getMyReminders()
                if (response && response.status == 200) {
                    this.reminders = response.data.reminders
                }
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        async handleDisplayEnableDeviceLoginPopup() {
            try {
                // 1. MASTER SWITCH: If already registered, NEVER show the popup.
                if (this.isDeviceLoginCredentialExists) return

                // 2. Check User Prefs Cooldowns (1 day / 7 days)
                if (getHideDeviceLoginPrompt()) return

                // 3. Check if browser supports WebAuthn and Check if biometric/platform hardware is actually available 
                // (e.g., does the laptop have Windows Hello/TouchID?)
                const isHardwareReady = await isPlatformAuthenticatorAvailable()
                if (!isHardwareReady) return

                // All conditions met: Show the popup!
                this.dialogEnableDeviceLogin = true
            } catch (error) {
                this.error = true
                this.errorMessage = error
            }
        },
        async onEnableDeviceSuccess(deviceName) {
            // 1. Close the registration modal
            this.dialogEnableDeviceLogin = false

             // 2. Show the success notification
            this.dialogOkOnlyTitle = "Success! This device is now trusted."
            this.dialogOkOnlyMsg = `Your <b>${deviceName || 'device'}</b> is now set up for biometric login. <br>You can now sign in faster without using a password.`
            this.dialogOkOnly = true

            // 3. Update the state so the popup doesn't show again in this session
            this.$store.commit("setIsDeviceLoginCredentialExists", true)

            //Clear local cooldowns since they successfully registered
            clearDeviceLoginCooldowns()
        },
        onEnableDeviceFailure() {
             //Close the registration modal
            this.dialogEnableDeviceLogin = false

            //Show the failure notification
            this.dialogOkOnlyTitle = "Registration Unsuccessful"
            this.dialogOkOnlyMsg = "We couldn't link your device at this time. This can happen if the biometric scan was interrupted or timed out. You can try again later in My Settings."
            this.dialogOkOnly = true
        }
    },
    async created() {
        this.getMyReminders()

        this.handleDisplayEnableDeviceLoginPopup()

        this.$root.$emit('setHeaderTitles', {
            display: true,
            isMainMenu: true,
            headerTitle: this.greetings,
            headerPageTitle: 'My Dashboard'
        })
    },
    computed: {
        favorites() {
            return this.$store.getters.favorites
        },
        isDeviceLoginCredentialExists() {
            return this.$store.state.isDeviceLoginCredentialExists || false
        },
        greetings() {  
            // Destructure name and reference reactive 'now' property
            const name = this.$store.state.firstName
            const hour = this.$moment(this.now).hour()

            if (hour < 12) {
                return `Good morning, ${name}!`
            } else if (hour < 18) {
                return `Good afternoon, ${name}!`
            } else {
                return `Good evening, ${name}!`
            }
        },

        //note, any changes on User Dashboard Theme due to changes in Team Roles or Attendance settings
        //will take effect only on user's next login
        dashboardWidgetsLarge() {
            if (this.$store.state.dashboardTheme && this.$store.state.dashboardTheme.widgets) {
                return this.$store.state.dashboardTheme.widgets.filter(x => x.isVisible && x.widgetSize == 'LARGE')
            }
        },
        dashboardWidgetsSmall() {
            if (this.$store.state.dashboardTheme && this.$store.state.dashboardTheme.widgets) {
                return this.$store.state.dashboardTheme.widgets.filter(x => x.isVisible && x.widgetSize == 'SMALL')
            }
        },
        widgetComponentMap() {
            return {
                MY_APPROVALS: 'my-approvals',
                MY_FAVORITES: 'my-favorites',
                MY_REQUESTS: 'my-requests',
                QUICK_ACTIONS: 'quick-actions',
                RECENT_TIME_LOGS: 'recent-time-logs',
                TEAM_STATUS_TODAY: 'team-status-today',
                UPCOMING_TEAM_EVENTS: 'upcoming-events'
            }
        },
    },
}
</script>
<style scoped>
.remarks{
    cursor: pointer;
}
.remarks:hover {
    text-decoration: underline;
}
.clock-button {
    margin:2px -3px 0px 3px;
}
.alert-link {
    cursor: pointer;
}
.alert-link:hover {
    text-decoration: underline;
}
</style>